import { notification } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    getApiError,
    getSchedulerGetMarketsWithoutRoute,
    getSchedulerSearchMarketsWithoutRoute,
} from "tm-shared/api";
import { MarketWithoutRoute } from "tm-shared/types";

import { MANAGER_AGENT_VOCABULARY } from "@/constants/domain/managerAgentVocabulary";
import { isManagerRole } from "@/hooks/isManagerRole";

export const useMarketsWithoutRoute = ({ regionId, role, partnerId }) => {
    const [markets, setMarkets] = useState<MarketWithoutRoute[]>([]);
    const [loading, setLoading] = useState(false);

    const abortRef = useRef<AbortController | null>(null);
    const requestIdRef = useRef(0);

    const fetchMarkets = useCallback(
        async (query?: string) => {
            if (!regionId) return;

            // 1) скасувати попередній запит
            abortRef.current?.abort();

            // 2) створити контролер для нового
            const controller = new AbortController();
            abortRef.current = controller;

            // 3) requestId щоб не перезатирати state старими відповідями
            const requestId = ++requestIdRef.current;

            setLoading(true);

            try {
                const commonParams = {
                    mainRegionId: regionId,
                    roleSlug: MANAGER_AGENT_VOCABULARY[role] ?? role,
                    partnerId: isManagerRole(role) ? partnerId : null,
                    axiosConfig: { signal: controller.signal },
                };

                const apiPromise =
                    query?.length >= 4
                        ? getSchedulerSearchMarketsWithoutRoute({
                              query,
                              ...commonParams,
                          })
                        : getSchedulerGetMarketsWithoutRoute(commonParams);

                const { result } = await apiPromise;

                // якщо це вже не останній запит — ігноруємо результат
                if (requestId !== requestIdRef.current) return;

                setMarkets(result);
            } catch (error) {
                const { displayMessage, isAborted } = getApiError(error);

                if (isAborted) return;

                notification.error({
                    message: "Не вдалося отримати маркети",
                    description: displayMessage,
                });
            } finally {
                // 5) loading вимикаємо тільки для останнього запиту
                if (requestId === requestIdRef.current) {
                    setLoading(false);
                }
            }
        },
        [regionId, role, partnerId],
    );

    useEffect(() => {
        fetchMarkets();

        return () => {
            // скасувати запит при unmount
            abortRef.current?.abort();
        };
    }, [fetchMarkets]);

    return { markets, loading, refetch: fetchMarkets };
};
